import type {
    AnatomyBinding,
    AnatomyPart,
    AnatomyState,
    ComponentAnatomySpec,
} from "./componentAnatomy";
import { menuAnatomyVariants } from "./componentAnatomyMenuVariants";
import { anatomyTextStyle } from "./componentAnatomyText";

// Bindings mined from designSystem/Menu/MenuPopup.module.scss and
// MenuItem.module.scss; row variants live in componentAnatomyMenuVariants.ts.
// The interaction state drives the first row: hover is [data-highlighted]
// (pointer or keyboard), pressed is :active, disabled is the row's disabled
// prop.

const parts: readonly AnatomyPart[] = [
    {
        id: "surface",
        label: "Popup surface",
        element:
            ".popup: fill, 1px border, radius, shadow; rows 4px inside the border, 2px apart",
    },
    {
        id: "sectionTitle",
        label: "Group label",
        element: ".groupLabel (Base UI GroupLabel), 28px",
    },
    {
        id: "itemBackground",
        label: "Item background",
        element: ".item, 28px row with --radius-xs",
    },
    {
        id: "glyph",
        label: "Leading icon",
        element:
            ".itemLeading, 16px slot; recipe-icon-color fills it with the row colour",
    },
    {
        id: "itemLabel",
        label: "Item label",
        element: ".itemLabel, single line with ellipsis",
    },
    {
        id: "indicator",
        label: "Trailing indicator",
        element:
            ".itemTrailing end slot: .itemTriangle (submenu trigger), SwitchIndicator in .itemSwitch (toggle item), CheckIcon in the 16px .itemCheckSlot (radio item)",
    },
    {
        id: "separator",
        label: "Separator",
        element:
            ".separator, full width between the popup borders, 4px above and below",
    },
];

const states: readonly AnatomyState[] = [
    { id: "resting", label: "Default", selector: "" },
    {
        id: "hover",
        label: "Highlighted",
        selector: " .item[data-highlighted]:not([data-disabled])",
    },
    {
        id: "pressed",
        label: "Pressed",
        selector: " .item:active:not([data-disabled])",
    },
    { id: "disabled", label: "Disabled", selector: " .item[data-disabled]" },
];

const sharedBindings: readonly AnatomyBinding[] = [
    { partId: "surface", property: "background-color", token: "--bg-primary" },
    { partId: "surface", property: "border-color", token: "--border-divider" },
    { partId: "surface", property: "border-width", token: "--border-width" },
    { partId: "surface", property: "border-radius", token: "--radius-m" },
    { partId: "surface", property: "box-shadow", token: "--shadow-2" },
    {
        partId: "surface",
        property: "padding",
        token: "--space-4",
        note: "Figma's 1px frame padding sits under the inside stroke, so rows are 4px inside the CSS border.",
    },
    {
        partId: "surface",
        property: "row-gap",
        token: "--space-2",
        note: "Groups and separators add 2px more, so they stack 4px apart.",
    },
    {
        partId: "surface",
        property: "max-width",
        literal: "min(var(--available-width), var(--menu-popup-max-width))",
        via: "--menu-popup-max-width",
        note: "320px by default; override on classNames.popup.",
    },
    ...anatomyTextStyle({
        scale: "xs",
        weight: "400",
        mixin: "text-xs-12-regular",
        partId: "sectionTitle",
    }),
    { partId: "sectionTitle", property: "color", token: "--fg-secondary" },
    {
        partId: "sectionTitle",
        property: "padding",
        literal: "5px var(--space-8)",
        note: "5px block padding is Figma's 28px title around 18px text.",
    },
    { partId: "itemBackground", property: "min-height", literal: "28px" },
    {
        partId: "itemBackground",
        property: "padding-inline",
        token: "--space-8",
    },
    {
        partId: "itemBackground",
        property: "border-radius",
        token: "--radius-xs",
    },
    { partId: "itemBackground", property: "gap", token: "--space-8" },
    { partId: "glyph", property: "width", literal: "16px" },
    { partId: "glyph", property: "height", literal: "16px" },
    ...anatomyTextStyle({
        scale: "s",
        weight: "400",
        mixin: "text-s-14-regular",
        partId: "itemLabel",
    }),
    { partId: "separator", property: "height", token: "--border-width" },
    {
        partId: "separator",
        property: "background-color",
        token: "--border-divider",
    },
    {
        partId: "separator",
        property: "margin",
        literal: "var(--space-2) calc(-1 * var(--space-4))",
        note: "Cancels the 4px inset so the line meets both borders; the 2px margin plus the 2px gap puts it 4px from the rows.",
    },
];

export const menuAnatomy: ComponentAnatomySpec = {
    label: "Menu",
    schematic: "menu",
    rootSelector: ".popup",
    variantAxisLabel: "Item",
    parts,
    states,
    sharedBindings,
    variants: menuAnatomyVariants,
};
